import { DebugLogger } from '@pique/debug';
import { UserFriendlyError } from '@pique/graphql';
import { fromPromise, Service } from '@toeverything/infra';

import { BackendError, NetworkError } from '../error';
import { addTauriAuthHeaders } from '../utils/tauri-auth';

export function getPiqueCloudBaseUrl(): string {
  let base: string;
  if (environment.isTauri) {
    base = runtimeConfig.serverUrlPrefix;
  } else {
    const { protocol, hostname, port } = window.location;
    base = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }
  try {
    (window as any).__PIQUE_BASE_URL__ = base;
  } catch {}
  return base;
}

export function getShareUrl(workspaceId: string, docId: string): string {
  // Short URL (re-enable when Dub quota available):
  // const shortKey = docId.substring(0, 8);
  // return `https://share.pique.so/${shortKey}`;
  return `${getPiqueCloudBaseUrl()}/share/${workspaceId}/${docId}`;
}

const logger = new DebugLogger('affine:fetch');

export type FetchInit = RequestInit & { timeout?: number };

export class FetchService extends Service {
  rxFetch = (
    input: string,
    init?: RequestInit & {
      // https://github.com/microsoft/TypeScript/issues/54472
      priority?: 'auto' | 'low' | 'high';
    } & {
      traceEvent?: string;
    }
  ) => {
    return fromPromise(signal => {
      return this.fetch(input, { signal, ...init });
    });
  };

  /**
   * fetch with custom custom timeout and error handling.
   */
  fetch = async (input: string, init?: FetchInit): Promise<Response> => {
    logger.debug('fetch', input);
    const externalSignal = init?.signal;
    if (externalSignal?.aborted) {
      throw externalSignal.reason;
    }
    const abortController = new AbortController();
    externalSignal?.addEventListener('abort', reason => {
      abortController.abort(reason);
    });

    const timeout = init?.timeout ?? 15000;
    const timeoutId = setTimeout(() => {
      abortController.abort('timeout');
    }, timeout);

    const headers = await addTauriAuthHeaders(init?.headers || {});

    const res = await fetch(new URL(input, getPiqueCloudBaseUrl()), {
      ...init,
      headers,
      signal: abortController.signal,
    }).catch(err => {
      logger.debug('network error', err);
      throw new NetworkError(err);
    });
    clearTimeout(timeoutId);
    if (res.status === 504) {
      const error = new Error('Gateway Timeout');
      logger.debug('network error', error);
      throw new NetworkError(error, res.status);
    }
    if (!res.ok) {
      logger.warn(
        'backend error',
        new Error(`${res.status} ${res.statusText}`)
      );
      let reason: string | Record<string, unknown> = '';
      if (res.headers.get('Content-Type')?.includes('application/json')) {
        try {
          reason = await res.json();
        } catch {
          // ignore
        }
      }
      throw new BackendError(
        UserFriendlyError.fromAnyError(reason),
        res.status
      );
    }
    return res;
  };
}
